import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  keyframes,
  useTheme,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useNavigate, useLocation } from 'react-router-dom';
import { Permissions, usePermission } from '@billda/permissions-ui';
import { Routes } from '../../../routes';
import { useOnboardingStatus } from '../hooks/useOnboardingStatus';
import { useCompleteOnboarding } from '../hooks/useCompleteOnboarding';
import { useAuthStore } from '../../auth/stores/authStore';
import { useShellStore } from '../../../shared/stores/shellStore';
import { SIDEBAR_WIDTH } from '../../../shared/components/layout/sidebar/constants';
import {
  TOTAL_STEPS,
  getCompletedCount,
  getNextStep,
  getLastCompletedStep,
} from '../utils/onboardingSteps';
import type { OnboardingStepKey } from '../utils/onboardingSteps';

const SUCCESS_INTERSTITIAL_MS = 2200;

const pulse = keyframes`
  0% { transform: scale(1); }
  40% { transform: scale(1.18); }
  100% { transform: scale(1); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.8; }
`;

const scaleIn = keyframes`
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); opacity: 1; }
`;

const contentFade = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const FAB_DESCRIPTIONS: Record<OnboardingStepKey, string> = {
  invite: 'Add workers so they can book work and get paid.',
  'import-site': 'Import a construction project to start tracking work.',
  'assign-sites': 'Control which workers can see and book work on each site.',
  'labour-groups': 'Organise trade categories so workers see the right items.',
  'assign-groups': 'Link each worker to their trade group.',
};

const EXPANDED_CONTENT_HEIGHT = 88;

interface ProgressRingProps {
  completedSteps: number;
  totalSteps: number;
  allComplete: boolean;
  accentColor: string;
  isDark: boolean;
  isPulsing: boolean;
  size: number;
}

const ProgressRing: React.FC<ProgressRingProps> = ({
  completedSteps,
  totalSteps,
  allComplete,
  accentColor,
  isDark,
  isPulsing,
  size,
}) => {
  const progressValue = (completedSteps / totalSteps) * 100;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        flexShrink: 0,
        animation: isPulsing ? `${pulse} 0.6s ease` : 'none',
      }}
      data-testid="getting-started-fab-progress"
    >
      <Box
        sx={{
          position: 'absolute',
          inset: -6,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${accentColor}20 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={3}
        sx={{
          color: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
        }}
      />
      <CircularProgress
        variant="determinate"
        value={progressValue}
        size={size}
        thickness={3}
        sx={{
          position: 'absolute',
          left: 0,
          color: accentColor,
          transition: 'color 0.3s ease',
          filter: `drop-shadow(0 0 4px ${accentColor}66)`,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '0.72rem',
            color: allComplete ? 'success.main' : 'text.primary',
            letterSpacing: '-0.02em',
          }}
        >
          {completedSteps}/{totalSteps}
        </Typography>
      </Box>
    </Box>
  );
};

export const GettingStartedFab: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuthStore();
  const hasOrgManage = usePermission(Permissions.Organisation.Manage);
  const { data: status } = useOnboardingStatus();
  const { mutate: complete } = useCompleteOnboarding();

  const sidebarCollapsed = useShellStore(state => state.sidebarCollapsed);

  const [isPulsing, setIsPulsing] = useState(false);
  const [showingSuccess, setShowingSuccess] = useState(false);
  const [completedStepName, setCompletedStepName] = useState('');
  const prevCompletedRef = useRef<number | null>(null);
  const successTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const completedSteps = status ? getCompletedCount(status) : 0;
  const totalSteps = TOTAL_STEPS;
  const allComplete = completedSteps === totalSteps;
  const nextStep = status ? getNextStep(status) : null;
  const isDark = theme.palette.mode === 'dark';

  // Detect step completion → show success interstitial
  useEffect(() => {
    if (
      prevCompletedRef.current !== null &&
      completedSteps > prevCompletedRef.current
    ) {
      const justCompleted = status ? getLastCompletedStep(status) : null;
      setCompletedStepName(justCompleted?.title ?? 'Step');
      setShowingSuccess(true);
      setIsPulsing(true);

      const pulseTimer = setTimeout(() => setIsPulsing(false), 600);
      successTimer.current = setTimeout(() => {
        setShowingSuccess(false);
      }, SUCCESS_INTERSTITIAL_MS);

      prevCompletedRef.current = completedSteps;
      return () => {
        clearTimeout(pulseTimer);
        clearTimeout(successTimer.current);
      };
    }
    prevCompletedRef.current = completedSteps;
  }, [completedSteps, status]);

  // Clean up success timer on unmount
  useEffect(() => {
    return () => clearTimeout(successTimer.current);
  }, []);

  const handleCtaClick = useCallback(() => {
    if (allComplete) {
      complete(undefined, {
        onSuccess: () => navigate(Routes.sites.manage, { replace: true }),
      });
    } else if (nextStep) {
      navigate(nextStep.ctaPath);
    }
  }, [allComplete, complete, navigate, nextStep]);

  // Visibility checks
  const isOnGettingStartedPage =
    location.pathname === Routes.onboarding.gettingStarted;
  const isInvitationFlow = location.pathname.startsWith('/invitation/');
  const isDownloadAppPage = location.pathname === Routes.downloadApp;
  const isOnboardingIncomplete = status && !status.gettingStartedCompletedAt;

  if (
    !isAuthenticated ||
    isInvitationFlow ||
    isDownloadAppPage ||
    !status ||
    !isOnboardingIncomplete ||
    !hasOrgManage ||
    isOnGettingStartedPage
  ) {
    return null;
  }

  const accentColor =
    allComplete || showingSuccess
      ? theme.palette.success.main
      : theme.palette.primary.main;

  return (
    <Box
      data-testid="getting-started-fab"
      sx={{
        position: 'fixed',
        bottom: 64,
        // Mobile: centered, Desktop: snug to left of content area (right of sidebar)
        left: {
          xs: '50%',
          md: sidebarCollapsed ? 32 : SIDEBAR_WIDTH + 32,
        },
        transform: {
          xs: 'translateX(-50%)',
          md: 'none',
        },
        transition: 'left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: theme.zIndex.fab,
        animation: `${fadeIn} 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards`,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: -4,
          borderRadius: '20px',
          background: `radial-gradient(ellipse at center, ${accentColor}22 0%, transparent 70%)`,
          animation: `${glowPulse} 3s ease-in-out infinite`,
          pointerEvents: 'none',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          bgcolor: isDark
            ? 'rgba(39, 43, 48, 0.92)'
            : 'rgba(255, 255, 255, 0.95)',
          borderRadius: '16px',
          px: 2.5,
          py: 2,
          border: '1px solid',
          borderColor: isDark
            ? 'rgba(255, 255, 255, 0.08)'
            : 'rgba(0, 0, 0, 0.06)',
          boxShadow: [
            `0 12px 40px rgba(0, 0, 0, ${isDark ? 0.5 : 0.15})`,
            `0 4px 12px rgba(0, 0, 0, ${isDark ? 0.3 : 0.08})`,
            `inset 0 1px 0 rgba(255, 255, 255, ${isDark ? 0.05 : 0.5})`,
          ].join(', '),
          backdropFilter: 'blur(16px)',
          width: 340,
        }}
      >
        {/* Top row: content + progress ring */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Left: text content — fixed height */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              height: EXPANDED_CONTENT_HEIGHT,
            }}
          >
            {/* Success interstitial */}
            {showingSuccess && !allComplete ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  height: '100%',
                  gap: 0.75,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    animation: `${contentFade} 0.3s ease both`,
                  }}
                >
                  <CheckCircleIcon
                    sx={{
                      fontSize: 11,
                      animation: `${scaleIn} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards`,
                    }}
                  />
                  Step complete
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    lineHeight: 1.2,
                    color: 'success.main',
                    letterSpacing: '-0.01em',
                    animation: `${contentFade} 0.3s ease 0.2s both`,
                  }}
                >
                  {completedStepName} — done!
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.4,
                    animation: `${contentFade} 0.3s ease 0.35s both`,
                  }}
                >
                  {completedSteps}/{totalSteps} steps complete
                </Typography>
              </Box>
            ) : (
              /* Normal step content */
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                  animation: `${contentFade} 0.25s ease both`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: accentColor,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <RocketLaunchIcon sx={{ fontSize: 11 }} />
                  {allComplete
                    ? 'Setup complete'
                    : `Step ${completedSteps + 1} of ${totalSteps}`}
                </Typography>

                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    lineHeight: 1.2,
                    color: allComplete ? 'success.main' : 'text.primary',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {allComplete ? "You're all set!" : nextStep?.title}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.4,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {allComplete
                    ? 'Your organisation is ready to go.'
                    : nextStep
                      ? FAB_DESCRIPTIONS[nextStep.key]
                      : undefined}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Right: progress ring */}
          <Box sx={{ flexShrink: 0, pt: 0.25 }}>
            <ProgressRing
              completedSteps={completedSteps}
              totalSteps={totalSteps}
              allComplete={allComplete}
              accentColor={accentColor}
              isDark={isDark}
              isPulsing={isPulsing}
              size={48}
            />
          </Box>
        </Box>

        {/* Bottom action row — always rendered for stable height, visibility toggled */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 1.5,
            pt: 1.5,
            borderTop: '1px solid',
            borderColor: isDark
              ? 'rgba(255, 255, 255, 0.06)'
              : 'rgba(0, 0, 0, 0.06)',
            opacity: showingSuccess ? 0 : 1,
            pointerEvents: showingSuccess ? 'none' : 'auto',
            transition: 'opacity 0.2s ease',
          }}
        >
          <Button
            size="small"
            variant="contained"
            color={allComplete ? 'success' : 'primary'}
            onClick={handleCtaClick}
            endIcon={
              allComplete ? (
                <CheckCircleOutlineIcon sx={{ fontSize: '14px !important' }} />
              ) : (
                <ArrowForwardIcon sx={{ fontSize: '14px !important' }} />
              )
            }
            sx={{
              fontWeight: 700,
              fontSize: '0.75rem',
              textTransform: 'none',
              borderRadius: '10px',
              px: 2,
              py: 0.75,
              backgroundSize: '200% 100%',
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                borderRadius: 'inherit',
                background:
                  'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)',
                backgroundSize: '200% 100%',
                animation: `${shimmer} 3s ease-in-out infinite`,
                pointerEvents: 'none',
              },
              position: 'relative',
              overflow: 'hidden',
            }}
            data-testid="getting-started-fab-cta"
          >
            {allComplete
              ? 'Go to my sites'
              : (nextStep?.ctaLabel ?? 'Continue')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
